import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  LoadingNextPage,
  ProjectMenuTileListContainer,
  TileToolbarContainer
} from '../../styled-component'
import { debounce, isEmpty } from 'lodash'
import { useOnInView } from 'react-intersection-observer'
import { useHistory } from 'react-router-dom'
import ProjectTileCard from '../../../../components/project-tile-card'
import ProjectMenuTileLoadList from './components/project-menu-tile-load-list'
import ProjectMenuTileToolbar from './components/project-menu-tile-toolbar'
import ProjectMenuTileEmptySearch from './components/project-menu-tile-empty-search'
import ProjectMenuTileEmptyProjectTree from './components/project-menu-tile-empty-project-tree'
import { useMeQuery } from '../../../../react-query/authentication/query/use-me-query'
import { projectMenuTabOptions } from '../../constants'
import {
  myProjectSortDirectionOptions,
  myProjectSortOptions,
  useGetMyProjectsQuery
} from '../../../../react-query/project/query/use-get-my-projects-query'
import { iconSizeOptions } from '../../../../components/stories/atoms/icons/icons-sizes'
import { useUserLeaveProjectMutation } from '../../../../react-query/project/mutation/use-user-leave-project-mutation'
import LeaveProjectAlert from '../leave-project-alert'
import { SpinnerGrey } from '../../../../components/stories/atoms/icons/special'
import { useProjectMenuStore } from '../../../../zustand/project-menu/use-project-menu-store'
import { useProjectMenuListViewControl } from '../../../../hooks/use-project-menu-list-view-control'

const emptyUserPermissions = []
const ProjectMenuTileMyProjects = () => {
  const history = useHistory()
  const [projectIdToLeave, setProjectIdToLeave] = useState(null)
  const search = useProjectMenuStore((state) => state.projectMenuSearch)
  const setSearch = useProjectMenuStore((state) => state.setProjectMenuSearch)

  const { viewType, tab } = useProjectMenuListViewControl()

  const queryParams = useMemo(
    () => ({
      search,
      sortDirection: myProjectSortDirectionOptions.DESC,
      projectSort: myProjectSortOptions.LAST_MODIFIED
    }),
    [search]
  )

  // queries
  const {
    data: myProjectList,
    isLoading: myProjectListLoading,
    isFetchingNextPage: nextPageIsLoading,
    fetchNextPage,
    hasNextPage
  } = useGetMyProjectsQuery({
    params: queryParams
  })

  const activeProjectId = useMemo(() => {
    const settingsMatch = location.pathname.match(
      /^\/project\/settings\/([^/]+)/
    )

    if (settingsMatch?.[1]) {
      return settingsMatch[1]
    }

    const projectMatch = location.pathname.match(/^\/project\/([^/]+)/)
    const matchedProjectId = projectMatch?.[1]

    if (
      !matchedProjectId ||
      ['move', 'newfolder', 'rename', 'settings'].includes(matchedProjectId)
    ) {
      return null
    }

    return matchedProjectId
  }, [location.pathname])

  const handleLeaveProjectSuccess = useCallback(
    (_data, { projectId }) => {
      if (activeProjectId === projectId) {
        history.push('/project')
      }
    },
    [activeProjectId, history]
  )
  const { data: user } = useMeQuery()

  // mutations
  const { mutate: leaveProject } = useUserLeaveProjectMutation({
    onSuccess: handleLeaveProjectSuccess
  })

  // derived states

  const projectList = useMemo(
    () => myProjectList?.pages.flatMap((p) => p.content) ?? [],
    [myProjectList]
  )

  const userPermissions = useMemo(
    () => user?.permissions ?? emptyUserPermissions,
    [user?.permissions]
  )

  const hasProjects = projectList.length > 0
  const isSearching = !isEmpty(search)

  const isEmptySearch = isSearching && !hasProjects
  const isEmptyState = !isSearching && !hasProjects

  const [inputValue, setInputValue] = useState(search)

  useEffect(() => {
    setInputValue(search)
  }, [search])

  // handlers
  const debouncedSetSearch = useMemo(() => debounce(setSearch, 400), [])

  const handleRequestLeaveProject = useCallback((project) => {
    setProjectIdToLeave(project.id)
  }, [])

  const handleCloseLeaveProjectAlert = useCallback(() => {
    setProjectIdToLeave(null)
  }, [])

  const handleConfirmLeaveProject = useCallback(
    (projectId) => {
      leaveProject({ projectId })
    },
    [leaveProject]
  )

  const handleSearch = useCallback(
    (value) => {
      setInputValue(value)
      debouncedSetSearch(value)
    },
    [debouncedSetSearch]
  )

  const handleCreateProject = () => {
    history.push(`/project/new?view=${viewType}&tab=${tab}`)
  }

  const openItem = (item) => {
    history.push(`/project/${item.id}`)
  }

  // infinite scroll
  const onBottomReached = useCallback(() => {
    if (!hasNextPage || nextPageIsLoading) return
    fetchNextPage()
  }, [hasNextPage, nextPageIsLoading])

  const onBottomReachedRef = useRef(onBottomReached)

  useEffect(() => {
    onBottomReachedRef.current = onBottomReached
  }, [onBottomReached])

  const bottomRef = useOnInView(
    useCallback((inView) => {
      if (inView) onBottomReachedRef.current?.()
    }, []),
    { rootMargin: '20px', skip: !hasNextPage }
  )

  // renders
  const ProjectTree = useCallback(
    () =>
      projectList.map((item) => {
        const metaData = {
          lastActivity: item.lastActivity,
          openPins: item.openPins,
          openTasks: item.openTasks
        }

        return (
          <ProjectTileCard
            key={item.id}
            item={item}
            userPermissions={userPermissions}
            onClick={() => openItem(item)}
            metaData={metaData}
            onLeaveProject={handleRequestLeaveProject}
            projectListOption={projectMenuTabOptions.MY_PROJECTS}
          />
        )
      }),
    [projectList]
  )

  const ProjectTileList = () => {
    if (isEmptySearch) return <ProjectMenuTileEmptySearch />

    return (
      <>
        {isEmptyState ? (
          <ProjectMenuTileEmptyProjectTree
            projectListOption={projectMenuTabOptions.MY_PROJECTS}
            handleCreateProject={handleCreateProject}
            userPermissions={userPermissions}
          />
        ) : (
          <ProjectTree />
        )}
      </>
    )
  }

  return (
    <>
      {projectIdToLeave && (
        <LeaveProjectAlert
          projectId={projectIdToLeave}
          onClose={handleCloseLeaveProjectAlert}
          onConfirm={handleConfirmLeaveProject}
        />
      )}
      <TileToolbarContainer>
        <ProjectMenuTileToolbar
          setSearch={handleSearch}
          search={inputValue}
          hideToggle
        />
      </TileToolbarContainer>

      <ProjectMenuTileListContainer>
        <ProjectMenuTileLoadList isLoading={myProjectListLoading}>
          <ProjectTileList />
        </ProjectMenuTileLoadList>
      </ProjectMenuTileListContainer>

      {nextPageIsLoading && (
        <LoadingNextPage>
          <SpinnerGrey size={iconSizeOptions['ICON-XL']} />
        </LoadingNextPage>
      )}

      <div ref={bottomRef} style={{ height: 1 }} />
    </>
  )
}

export default ProjectMenuTileMyProjects
